import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { BarChart3, Download, X, ExternalLink, Info, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const TABS = [
  { key: "projects", label: "Projetos", ep: "/reports/projects" },
  { key: "tasks", label: "Tarefas", ep: "/reports/tasks" },
  { key: "sprints", label: "Sprints", ep: "/reports/sprints" },
  { key: "recurrences", label: "Recorrências", ep: "/reports/recurrences" },
  { key: "activity", label: "Atividade e contribuição", ep: "/reports/activity" },
];
const PAGE = 12;
const STATUS_LABELS = { open: "Aberta", done: "Concluída", cancelled: "Cancelada" };
const PRIORITY_LABELS = { low: "Baixa", medium: "Média", high: "Alta", urgent: "Urgente" };
const SPRINT_STATUS = { planned: "Planejada", active: "Em andamento", completed: "Concluída", cancelled: "Cancelada", archived: "Arquivada" };
const REC_STATUS = { active: "Ativa", paused: "Pausada", ended: "Encerrada", needs_review: "Revisão necessária" };

const iso30 = () => new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10);
const isoToday = () => new Date().toISOString().slice(0, 10);
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("pt-BR") : "—");
const fmtDay = (s) => { if (!s) return "—"; try { return new Date(s.length <= 10 ? s + "T00:00:00" : s).toLocaleDateString("pt-BR"); } catch { return s; } };

export default function Reports() {
  const [tab, setTab] = useState("projects");
  const [options, setOptions] = useState({ spaces: [], projects: [], users: [], departments: [], can_compare: true });
  const [filters, setFilters] = useState({ start: iso30(), end: isoToday() });
  const [data, setData] = useState(null);
  const [dataTab, setDataTab] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [page, setPage] = useState(1);
  const [burndown, setBurndown] = useState(null);
  const loadSeq = useRef(0);

  useEffect(() => {
    api.get("/reports/options").then(({ data }) => setOptions(data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const qs = useCallback(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v != null) params.append(k, v); });
    return params.toString();
  }, [filters]);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    const reqTab = tab;
    setLoading(true); setErr(null);
    try {
      const ep = TABS.find((t) => t.key === reqTab).ep;
      const { data } = await api.get(`${ep}?${qs()}`);
      if (seq !== loadSeq.current) return; // stale response — a newer load started
      setData(data); setDataTab(reqTab); setPage(1);
    } catch (e) {
      if (seq !== loadSeq.current) return;
      setErr(formatApiError(e)); setData(null);
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [tab, qs]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const ep = TABS.find((t) => t.key === tab).ep;
      const res = await api.get(`${ep}?${qs()}&format=csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `relatorio_${tab}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearFilters = () => setFilters({ start: iso30(), end: isoToday() });

  const projectsForSpace = useMemo(() => {
    if (!filters.workspace_id) return options.projects;
    return options.projects.filter((p) => p.workspace_id === filters.workspace_id);
  }, [filters.workspace_id, options.projects]);

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Análise</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-2 flex items-center gap-2"><BarChart3 className="w-7 h-7 text-slate-400" /> Relatórios</h1>
          <p className="text-slate-500 mt-1">Relatórios somente leitura. Respeitam exatamente as permissões e a visibilidade atuais.</p>
        </div>
        <button onClick={exportCsv} data-testid="reports-export-csv" className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50"><Download className="w-4 h-4" /> Exportar CSV</button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto" data-testid="reports-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => { setTab(t.key); setData(null); setErr(null); }} data-testid={`reports-tab-${t.key}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${tab === t.key ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}>{t.label}</button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid="reports-filters">
        <div className="grid sm:grid-cols-3 lg:grid-cols-4 gap-3">
          <Filt label="Período inicial"><input type="date" value={filters.start || ""} onChange={(e) => setF("start", e.target.value)} data-testid="filter-start" className="rfield" /></Filt>
          <Filt label="Período final"><input type="date" value={filters.end || ""} onChange={(e) => setF("end", e.target.value)} data-testid="filter-end" className="rfield" /></Filt>
          <Filt label="Espaço"><select value={filters.workspace_id || ""} onChange={(e) => { setF("workspace_id", e.target.value); setF("project_id", ""); }} data-testid="filter-space" className="rfield"><option value="">Todos</option>{options.spaces.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></Filt>
          <Filt label="Tipo de espaço"><select value={filters.space_type || ""} onChange={(e) => setF("space_type", e.target.value)} data-testid="filter-space-type" className="rfield"><option value="">Todos</option><option value="department">Departamental</option><option value="interdepartmental">Interdepartamental</option></select></Filt>
          <Filt label="Projeto"><select value={filters.project_id || ""} onChange={(e) => setF("project_id", e.target.value)} data-testid="filter-project" className="rfield"><option value="">Todos</option>{projectsForSpace.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></Filt>
          {(tab === "tasks" || tab === "activity") && (
            <Filt label="Responsável"><select value={filters.assignee || ""} onChange={(e) => setF("assignee", e.target.value)} data-testid="filter-assignee" className="rfield"><option value="">Todos</option>{options.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select></Filt>
          )}
          {tab === "tasks" && <>
            <Filt label="Status"><select value={filters.status || ""} onChange={(e) => setF("status", e.target.value)} data-testid="filter-status" className="rfield"><option value="">Todos</option>{Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Filt>
            <Filt label="Prioridade"><select value={filters.priority || ""} onChange={(e) => setF("priority", e.target.value)} className="rfield"><option value="">Todas</option>{Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Filt>
            <Filt label="E/D/O/Q"><select value={filters.classification || ""} onChange={(e) => setF("classification", e.target.value)} className="rfield"><option value="">Todos</option>{["E", "D", "O", "Q"].map((c) => <option key={c} value={c}>{c}</option>)}</select></Filt>
            <Filt label="Recorrente"><select value={filters.recurring || ""} onChange={(e) => setF("recurring", e.target.value)} data-testid="filter-recurring" className="rfield"><option value="">Todas</option><option value="true">Recorrentes</option><option value="false">Não recorrentes</option></select></Filt>
          </>}
          {tab === "projects" && <Filt label="Status"><select value={filters.status || ""} onChange={(e) => setF("status", e.target.value)} data-testid="filter-status" className="rfield"><option value="">Todos</option><option value="planning">Planejamento</option><option value="in_progress">Em andamento</option><option value="paused">Pausado</option><option value="completed">Concluído</option><option value="archived">Arquivado</option></select></Filt>}
          {tab === "sprints" && <Filt label="Status"><select value={filters.sprint_status || ""} onChange={(e) => setF("sprint_status", e.target.value)} data-testid="filter-sprint-status" className="rfield"><option value="">Todos</option>{Object.entries(SPRINT_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Filt>}
        </div>
        <div className="flex justify-end mt-3"><button onClick={clearFilters} data-testid="filter-clear" className="text-sm text-slate-500 hover:text-slate-800">Limpar filtros</button></div>
      </div>

      {data?.partial && dataTab === tab && (
        <div className="flex items-center gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="partial-notice">
          <Info className="w-4 h-4" /> Indicadores calculados com base nos registros que você pode visualizar.
        </div>
      )}

      {loading ? <div className="text-center text-slate-500 py-12">Carregando...</div>
        : err ? <div className="text-center text-rose-600 py-12" data-testid="reports-error">{err}</div>
          : !data || dataTab !== tab ? <div className="text-center text-slate-400 py-12">Carregando...</div>
            : tab === "projects" ? <ProjectsTab data={data} page={page} setPage={setPage} />
              : tab === "tasks" ? <TasksTab data={data} page={page} setPage={setPage} />
                : tab === "sprints" ? <SprintsTab data={data} page={page} setPage={setPage} onBurndown={(s) => setBurndown(s)} query={qs()} filters={filters} />
                  : tab === "recurrences" ? <RecurrencesTab data={data} page={page} setPage={setPage} />
                    : <ActivityTab data={data} page={page} setPage={setPage} query={qs()} exportCsv={exportCsv} />}

      {burndown && <BurndownModal sprint={burndown} onClose={() => setBurndown(null)} />}
    </div>
  );
}

/* ----------------------------- shared UI ----------------------------- */
const Filt = ({ label, children }) => (
  <label className="block"><span className="block text-xs font-medium text-slate-500 mb-1">{label}</span>{children}</label>
);
const Card = ({ label, value, accent }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid={`card-${label}`}>
    <div className="text-2xl font-heading font-semibold tracking-tight" style={accent ? { color: accent } : {}}>{value ?? "—"}</div>
    <div className="text-xs text-slate-500 mt-0.5">{label}</div>
  </div>
);
const Cards = ({ children }) => <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="reports-cards">{children}</div>;

function BarChart({ title, data, labels }) {
  const max = Math.max(1, ...Object.values(data || {}));
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-slate-700 mb-3">{title}</h3>
      {Object.keys(data || {}).length === 0 ? <p className="text-xs text-slate-400">Sem dados.</p> : (
        <div className="space-y-2">
          {Object.entries(data).map(([k, v]) => (
            <div key={k} className="flex items-center gap-2 text-xs">
              <span className="w-28 truncate text-slate-600">{(labels && labels[k]) || k}</span>
              <div className="flex-1 bg-slate-100 rounded h-4 overflow-hidden"><div className="h-4 bg-slate-800 rounded" style={{ width: `${(v / max) * 100}%` }} /></div>
              <span className="w-8 text-right text-slate-500">{v}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Table({ headers, children, empty }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="report-table">
          <thead><tr className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">{headers.map((h) => <th key={h} className="px-4 py-3 whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-100">{children}</tbody>
        </table>
      </div>
      {empty && <div className="p-10 text-center text-sm text-slate-400">Nenhum registro encontrado.</div>}
    </div>
  );
}

function Pager({ page, setPage, total }) {
  const pages = Math.ceil(total / PAGE);
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-3 text-sm" data-testid="report-pager">
      <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1 rounded border border-slate-200 disabled:opacity-40">Anterior</button>
      <span className="text-slate-500">{page} / {pages}</span>
      <button disabled={page >= pages} onClick={() => setPage(page + 1)} className="px-3 py-1 rounded border border-slate-200 disabled:opacity-40">Próxima</button>
    </div>
  );
}

const slice = (rows, page) => rows.slice((page - 1) * PAGE, page * PAGE);

/* ----------------------------- PROJECTS ----------------------------- */
function ProjectsTab({ data, page, setPage }) {
  const s = data.summary;
  return (
    <div className="space-y-4">
      <Cards>
        <Card label="Projetos acessíveis" value={s.total} />
        <Card label="Planejamento" value={s.planning} />
        <Card label="Em andamento" value={s.in_progress} />
        <Card label="Pausados" value={s.paused} />
        <Card label="Concluídos" value={s.completed} />
        <Card label="Arquivados" value={s.archived} />
        <Card label="Com tarefas atrasadas" value={s.with_overdue} accent="#dc2626" />
        <Card label="Com tarefas impedidas" value={s.with_impeded} accent="#ea580c" />
      </Cards>
      <BarChart title="Projetos por status" data={data.by_status} labels={{ planning: "Planejamento", in_progress: "Em andamento", paused: "Pausados", completed: "Concluídos", archived: "Arquivados" }} />
      <Table headers={["Projeto", "Espaço", "Tipo", "Status", "Progresso", "Abertas", "Concluídas", "Atrasadas", "Impedidas", "Sem resp.", "Sprint ativa", "Conclusão", ""]} empty={data.rows.length === 0}>
        {slice(data.rows, page).map((r) => (
          <tr key={r.id} className="hover:bg-slate-50" data-testid={`project-row-${r.id}`}>
            <td className="px-4 py-3 font-medium text-slate-900">{r.name}</td>
            <td className="px-4 py-3 text-slate-600">{r.workspace_name}</td>
            <td className="px-4 py-3 text-slate-600">{r.space_type === "interdepartmental" ? "E.I." : "Depto"}</td>
            <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700">{({ planning: "Planejamento", in_progress: "Em andamento", paused: "Pausado", completed: "Concluído", archived: "Arquivado" })[r.status]}</span></td>
            <td className="px-4 py-3 text-slate-600">{r.progress}%</td>
            <td className="px-4 py-3 text-slate-600">{r.open_tasks}</td>
            <td className="px-4 py-3 text-slate-600">{r.done_tasks}</td>
            <td className="px-4 py-3 text-rose-600">{r.overdue_tasks}</td>
            <td className="px-4 py-3 text-amber-600">{r.impeded_tasks}</td>
            <td className="px-4 py-3 text-slate-600">{r.unassigned_tasks}</td>
            <td className="px-4 py-3 text-slate-500">{r.active_sprint || "—"}</td>
            <td className="px-4 py-3 text-slate-500">{fmtDate(r.completed_at)}</td>
            <td className="px-4 py-3"><Link to={`/projetos/${r.id}`} className="text-slate-400 hover:text-slate-800" data-testid={`project-link-${r.id}`}><ExternalLink className="w-4 h-4" /></Link></td>
          </tr>
        ))}
      </Table>
      <Pager page={page} setPage={setPage} total={data.rows.length} />
    </div>
  );
}

/* ----------------------------- TASKS ----------------------------- */
function TasksTab({ data, page, setPage }) {
  const s = data.summary;
  return (
    <div className="space-y-4">
      <Cards>
        <Card label="Criadas no período" value={s.created} />
        <Card label="Concluídas no período" value={s.completed} />
        <Card label="Abertas" value={s.open} />
        <Card label="Atrasadas" value={s.overdue} accent="#dc2626" />
        <Card label="Impedidas" value={s.impeded} accent="#ea580c" />
        <Card label="Canceladas" value={s.cancelled} />
        <Card label="Reabertas" value={s.reopened} />
        <Card label="Sem responsável" value={s.unassigned} />
        <Card label="Recorrentes" value={s.recurring} />
        <Card label="Story Points concluídos" value={s.story_points_done} />
        <Card label="Horas estim. (abertas)" value={s.estimated_hours_open} />
        <Card label={`Ciclo médio (${s.cycle_base}) `} value={s.avg_cycle_days != null ? `${s.avg_cycle_days}d` : "Dados insuf."} />
        <Card label={`% no prazo (${s.on_time_base})`} value={s.on_time_pct != null ? `${s.on_time_pct}%` : "Dados insuf."} />
      </Cards>
      <div className="grid lg:grid-cols-3 gap-4">
        <BarChart title="Por status" data={data.by_status} labels={STATUS_LABELS} />
        <BarChart title="Por prioridade" data={data.by_priority} labels={PRIORITY_LABELS} />
        <BarChart title="Por E/D/O/Q" data={data.by_class} />
      </div>
      <Table headers={["Título", "Projeto", "Responsável", "Status", "Localização", "Prior.", "EDOQ", "Prazo", "Atras.", "Imp.", "Rec.", "SP", "Criação", "Conclusão", ""]} empty={data.rows.length === 0}>
        {slice(data.rows, page).map((r) => (
          <tr key={r.id} className="hover:bg-slate-50" data-testid={`task-row-${r.id}`}>
            <td className="px-4 py-3 font-medium text-slate-900 max-w-[220px] truncate">{r.title}</td>
            <td className="px-4 py-3 text-slate-600">{r.project_name}</td>
            <td className="px-4 py-3 text-slate-600">{r.assignee_name || "—"}</td>
            <td className="px-4 py-3 text-slate-600">{STATUS_LABELS[r.status]}</td>
            <td className="px-4 py-3 text-slate-500">{r.location}</td>
            <td className="px-4 py-3 text-slate-500">{PRIORITY_LABELS[r.priority]}</td>
            <td className="px-4 py-3 text-slate-500">{r.classification}</td>
            <td className="px-4 py-3 text-slate-500">{fmtDay(r.due_date)}</td>
            <td className="px-4 py-3">{r.overdue ? <span className="text-rose-600">Sim</span> : "Não"}</td>
            <td className="px-4 py-3">{r.impeded ? <span className="text-amber-600">Sim</span> : "Não"}</td>
            <td className="px-4 py-3">{r.recurring ? "Sim" : "Não"}</td>
            <td className="px-4 py-3 text-slate-600">{r.story_points ?? "—"}</td>
            <td className="px-4 py-3 text-slate-500">{fmtDate(r.created_at)}</td>
            <td className="px-4 py-3 text-slate-500">{fmtDate(r.completed_at)}</td>
            <td className="px-4 py-3"><Link to={`/projetos/${r.project_id}/backlog`} className="text-slate-400 hover:text-slate-800" data-testid={`task-link-${r.id}`}><ExternalLink className="w-4 h-4" /></Link></td>
          </tr>
        ))}
      </Table>
      <Pager page={page} setPage={setPage} total={data.rows.length} />
    </div>
  );
}

/* ----------------------------- SPRINTS ----------------------------- */
function SprintsTab({ data, page, setPage, onBurndown, query, filters }) {
  const s = data.summary;
  const [selSprint, setSelSprint] = useState("");
  useEffect(() => {
    if (data.rows.length && !data.rows.find((r) => r.id === selSprint)) setSelSprint(data.rows[0].id);
    if (!data.rows.length) setSelSprint("");
  }, [data.rows]); // eslint-disable-line
  const selRow = data.rows.find((r) => r.id === selSprint);
  return (
    <div className="space-y-6">
      <Cards>
        <Card label="Concluídas no período" value={s.completed_in_period} />
        <Card label="Tarefas planejadas" value={s.planned_tasks} />
        <Card label="Tarefas entregues" value={s.delivered_tasks} />
        <Card label="Não entregues" value={s.not_delivered_tasks} />
        <Card label="SP planejados" value={s.planned_points} />
        <Card label="SP entregues" value={s.delivered_points} />
        <Card label="% entrega" value={`${s.delivery_pct}%`} />
        <Card label="Mudanças de escopo" value={s.scope_changes} />
        <Card label={`Velocity média (${s.velocity_base})`} value={s.avg_velocity != null ? s.avg_velocity : "Dados insuf."} />
      </Cards>

      {/* Velocity por Sprint */}
      <VelocitySection query={query} projectId={filters?.project_id} />

      {/* Burndown da Sprint */}
      <section className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid="burndown-section">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <h3 className="text-sm font-semibold text-slate-700">Burndown da Sprint</h3>
          <select value={selSprint} onChange={(e) => setSelSprint(e.target.value)} data-testid="burndown-sprint-select" className="rfield max-w-xs">
            {data.rows.length === 0 && <option value="">Nenhuma Sprint</option>}
            {data.rows.map((r) => <option key={r.id} value={r.id}>{r.name} — {r.project_name}</option>)}
          </select>
        </div>
        {selRow ? <BurndownView sprintId={selRow.id} /> : <p className="text-sm text-slate-400 py-6 text-center">Nenhuma Sprint disponível para os filtros selecionados.</p>}
      </section>

      {/* Tabela detalhada */}
      <Table headers={["Sprint", "Projeto", "Status", "Início", "Fim", "Escopo ini.", "Adic.", "Retir.", "Entreg.", "Não ent.", "SP ini.", "SP entr.", "% entr.", "Burndown", ""]} empty={data.rows.length === 0}>
        {slice(data.rows, page).map((r) => (
          <tr key={r.id} className="hover:bg-slate-50" data-testid={`sprint-row-${r.id}`}>
            <td className="px-4 py-3 font-medium text-slate-900">{r.name}</td>
            <td className="px-4 py-3 text-slate-600">{r.project_name}</td>
            <td className="px-4 py-3 text-slate-600">{SPRINT_STATUS[r.status] || r.status || "—"}</td>
            <td className="px-4 py-3 text-slate-500">{fmtDay(r.start_date)}</td>
            <td className="px-4 py-3 text-slate-500">{fmtDay(r.end_date)}</td>
            <td className="px-4 py-3 text-slate-600">{r.scope_initial}</td>
            <td className="px-4 py-3 text-slate-600">{r.added}</td>
            <td className="px-4 py-3 text-slate-600">{r.removed}</td>
            <td className="px-4 py-3 text-emerald-600">{r.delivered}</td>
            <td className="px-4 py-3 text-slate-600">{r.not_delivered}</td>
            <td className="px-4 py-3 text-slate-600">{r.planned_points}</td>
            <td className="px-4 py-3 text-slate-600">{r.delivered_points}</td>
            <td className="px-4 py-3 text-slate-600">{r.pct}%</td>
            <td className="px-4 py-3"><button onClick={() => onBurndown(r)} data-testid={`burndown-btn-${r.id}`} className="text-xs text-slate-700 underline hover:text-slate-900">Ver</button></td>
            <td className="px-4 py-3"><Link to={`/projetos/${r.project_id}/sprints`} className="text-slate-400 hover:text-slate-800" data-testid={`sprint-link-${r.id}`}><ExternalLink className="w-4 h-4" /></Link></td>
          </tr>
        ))}
      </Table>
      <Pager page={page} setPage={setPage} total={data.rows.length} />
    </div>
  );
}

function BurndownModal({ sprint, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 grid place-items-center p-4" onClick={onClose} data-testid="burndown-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl border border-slate-200 shadow-xl w-full max-w-2xl">
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-slate-200">
          <h2 className="font-heading text-lg font-semibold">Burndown — {sprint.name}</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-6"><BurndownView sprintId={sprint.id} /></div>
      </div>
    </div>
  );
}

// Shared burndown content: mode toggle + chart / insufficient / coverage states.
function BurndownView({ sprintId }) {
  const [mode, setMode] = useState("tasks");
  const [bd, setBd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    setLoading(true); setError(null);
    api.get(`/reports/sprints/${sprintId}/burndown?mode=${mode}`)
      .then(({ data }) => setBd(data))
      .catch((e) => { setBd(null); setError(e?.response?.status === 404 ? "Esta Sprint não está mais disponível." : "Não foi possível carregar o burndown."); })
      .finally(() => setLoading(false));
  }, [sprintId, mode]);
  return (
    <div data-testid="burndown-view">
      <div className="flex gap-2 mb-4">
        <button onClick={() => setMode("tasks")} data-testid="burndown-mode-tasks" className={`px-3 py-1.5 rounded-lg text-sm border ${mode === "tasks" ? "bg-slate-900 text-white border-slate-900" : "border-slate-200"}`}>Por tarefas</button>
        <button onClick={() => setMode("points")} data-testid="burndown-mode-points" className={`px-3 py-1.5 rounded-lg text-sm border ${mode === "points" ? "bg-slate-900 text-white border-slate-900" : "border-slate-200"}`}>Por Story Points</button>
      </div>
      {loading ? <p className="text-sm text-slate-500 py-8 text-center">Carregando...</p>
        : error ? <p className="text-sm text-rose-600 py-8 text-center" data-testid="burndown-error">{error}</p>
          : !bd ? <p className="text-sm text-rose-600 py-8 text-center">Não foi possível carregar o burndown.</p>
            : !bd.sufficient ? (
              <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-3" data-testid="burndown-insufficient">
                <AlertTriangle className="w-4 h-4 mt-0.5" /> {bd.message}
              </div>
            ) : <BurndownChart bd={bd} mode={mode} />}
      {bd?.points_coverage && (
        <p className="text-xs text-slate-500 mt-3" data-testid="burndown-coverage">Cobertura de estimativa: {bd.points_coverage.with_sp} com SP / {bd.points_coverage.without_sp} sem SP ({bd.points_coverage.pct}%).</p>
      )}
      <p className="text-xs text-slate-400 mt-2">Linha ideal em dias corridos. A linha real usa eventos históricos registrados (escopo inicial, adições, retiradas, conclusões, reaberturas, cancelamentos, alterações de SP).</p>
    </div>
  );
}

// Velocity per completed sprint (requires a selected project).
function VelocitySection({ query, projectId }) {
  const [vel, setVel] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!projectId) { setVel(null); return; }
    setLoading(true);
    api.get(`/reports/velocity?${query}`).then(({ data }) => setVel(data)).catch(() => setVel(null)).finally(() => setLoading(false));
  }, [query, projectId]);
  const max = Math.max(1, ...(vel?.series || []).map((x) => x.velocity));
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid="velocity-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-700">Velocity por Sprint</h3>
        <span className="text-xs text-slate-400">Story Points entregues · sprints concluídas</span>
      </div>
      {!projectId ? (
        <p className="text-sm text-slate-400 py-6 text-center" data-testid="velocity-need-project">Selecione um projeto no filtro para ver a velocity por Sprint.</p>
      ) : loading ? <p className="text-sm text-slate-500 py-6 text-center">Carregando...</p>
        : !vel || vel.series.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center" data-testid="velocity-empty">Não há Sprints concluídas com Story Points entregues para os filtros selecionados.</p>
        ) : (
          <div data-testid="velocity-chart">
            <div className="flex items-end gap-3 h-40 px-2 overflow-x-auto">
              {vel.series.map((x) => (
                <div key={x.sprint_id} className="flex flex-col items-center gap-1 min-w-[52px] group relative">
                  <div className="text-[10px] text-slate-500">{x.velocity}</div>
                  <div className="w-8 bg-slate-800 rounded-t" style={{ height: `${(x.velocity / max) * 120}px` }} title={`Total ${x.velocity} · inicial ${x.velocity_initial} · adicionadas ${x.velocity_added}`} />
                  <div className="text-[10px] text-slate-600 max-w-[60px] truncate" title={x.name}>{x.name}</div>
                  <div className="hidden group-hover:block absolute bottom-full mb-1 z-10 bg-slate-900 text-white text-[10px] rounded px-2 py-1 whitespace-nowrap">Inicial: {x.velocity_initial} · Adicionadas: {x.velocity_added} · {x.delivered_tasks} tarefa(s)</div>
                </div>
              ))}
            </div>
            {vel.avg != null && <p className="text-xs text-slate-500 mt-3">Velocity média: {vel.avg} SP em {vel.series.length} Sprint(s). Sem previsão automática de capacidade futura.</p>}
          </div>
        )}
    </section>
  );
}

function BurndownChart({ bd, mode }) {
  const W = 560, H = 240, P = 30;
  const series = bd.series;
  const maxV = Math.max(1, bd.initial, ...series.map((d) => d.remaining));
  const n = series.length;
  const x = (i) => P + (n <= 1 ? 0 : (i / (n - 1)) * (W - 2 * P));
  const y = (v) => H - P - (v / maxV) * (H - 2 * P);
  const realPath = series.map((d, i) => `${i === 0 ? "M" : "L"}${x(i)},${y(d.remaining)}`).join(" ");
  const idealPath = series.map((d, i) => `${i === 0 ? "M" : "L"}${x(i)},${y(d.ideal)}`).join(" ");
  return (
    <div data-testid="burndown-chart">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
        <line x1={P} y1={H - P} x2={W - P} y2={H - P} stroke="#cbd5e1" />
        <line x1={P} y1={P} x2={P} y2={H - P} stroke="#cbd5e1" />
        <path d={idealPath} fill="none" stroke="#94a3b8" strokeDasharray="4 4" strokeWidth="2" />
        <path d={realPath} fill="none" stroke="#0f172a" strokeWidth="2.5" />
        {series.map((d, i) => <circle key={i} cx={x(i)} cy={y(d.remaining)} r="2.5" fill="#0f172a" />)}
        <text x={P} y={P - 10} fontSize="11" fill="#64748b">{mode === "points" ? "Story Points restantes" : "Tarefas restantes"}</text>
      </svg>
      <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
        <span className="inline-flex items-center gap-1"><span className="w-4 h-0.5 bg-slate-900 inline-block" /> Real</span>
        <span className="inline-flex items-center gap-1"><span className="w-4 h-0.5 bg-slate-400 inline-block" style={{ borderTop: "2px dashed" }} /> Ideal</span>
        <span>Início {bd.initial} · {n} dia(s)</span>
        {bd.markers?.length > 0 && <span>{bd.markers.length} evento(s) de escopo/reabertura</span>}
      </div>
    </div>
  );
}

/* ----------------------------- RECURRENCES ----------------------------- */
function RecurrencesTab({ data, page, setPage }) {
  const s = data.summary;
  return (
    <div className="space-y-4">
      <Cards>
        <Card label="Modelos ativos" value={s.active} />
        <Card label="Pausados" value={s.paused} />
        <Card label="Encerrados" value={s.ended} />
        <Card label="Revisão necessária" value={s.needs_review} accent="#e11d48" />
        <Card label="Ocorrências geradas" value={s.occurrences} />
        <Card label="Ocorrências abertas" value={s.occ_open} />
        <Card label="Ocorrências concluídas" value={s.occ_done} />
        <Card label="Ocorrências atrasadas" value={s.occ_overdue} accent="#dc2626" />
        <Card label="Sem responsável" value={s.occ_unassigned} />
        <Card label="Ignoradas (sobreposição)" value={s.skipped_overlap} />
        <Card label="Ignoradas (projeto)" value={s.skipped_project} />
        <Card label={`Cumprimento (${s.fulfillment_base})`} value={s.fulfillment_pct != null ? `${s.fulfillment_pct}%` : "Dados insuf."} />
      </Cards>
      <Table headers={["Modelo", "Projeto", "Frequência", "Status", "Última", "Próxima", "Geradas", "Concluídas", "Atrasadas", "Sem resp.", ""]} empty={data.rows.length === 0}>
        {slice(data.rows, page).map((r) => (
          <tr key={r.id} className="hover:bg-slate-50" data-testid={`recurrence-report-row-${r.id}`}>
            <td className="px-4 py-3 font-medium text-slate-900">{r.title}</td>
            <td className="px-4 py-3 text-slate-600">{r.project_name}</td>
            <td className="px-4 py-3 text-slate-600">{r.frequency_summary}</td>
            <td className="px-4 py-3 text-slate-600">{REC_STATUS[r.status]}</td>
            <td className="px-4 py-3 text-slate-500">{fmtDate(r.last_occurrence_at)}</td>
            <td className="px-4 py-3 text-slate-500">{fmtDay(r.next_run_date)}</td>
            <td className="px-4 py-3 text-slate-600">{r.generated_count}</td>
            <td className="px-4 py-3 text-emerald-600">{r.occ_done}</td>
            <td className="px-4 py-3 text-rose-600">{r.occ_overdue}</td>
            <td className="px-4 py-3 text-slate-600">{r.occ_unassigned}</td>
            <td className="px-4 py-3"><Link to={`/projetos/${r.project_id}/recorrencias`} className="text-slate-400 hover:text-slate-800" data-testid={`recurrence-report-link-${r.id}`}><ExternalLink className="w-4 h-4" /></Link></td>
          </tr>
        ))}
      </Table>
      <Pager page={page} setPage={setPage} total={data.rows.length} />
    </div>
  );
}

/* ----------------------------- ACTIVITY ----------------------------- */
function ActivityTab({ data, page, setPage, query, exportCsv }) {
  if (!data.can_compare) {
    // member / viewer: only own activity, no peer comparison
    const r = data.rows[0];
    return (
      <div className="space-y-4">
        {!r ? <div className="text-center text-slate-400 py-8" data-testid="my-activity-empty-cards">Sem indicadores no período.</div> : (
          <>
            <h3 className="font-heading text-lg font-semibold" data-testid="my-activity-title">Minha atividade</h3>
            <Cards>
              <Card label="Atribuídas agora" value={r.assigned_now} />
              <Card label="Concluídas" value={r.completed} />
              <Card label="Atrasadas" value={r.overdue} accent="#dc2626" />
              <Card label="Story Points" value={r.story_points} />
              <Card label="Reabertas" value={r.reopened} />
              <Card label="Impedidas" value={r.impeded} />
              <Card label="Participações" value={r.participations} />
              <Card label="Recorrentes concl." value={r.rec_done} />
              <Card label={`% no prazo (${r.on_time_base})`} value={r.on_time_pct != null ? `${r.on_time_pct}%` : "Dados insuf."} />
              <Card label={`Ciclo médio (${r.cycle_base})`} value={r.avg_cycle_days != null ? `${r.avg_cycle_days}d` : "Dados insuf."} />
            </Cards>
          </>
        )}
        <MyActivityTable query={query} exportCsv={exportCsv} />
        <ActivityNote note={data.note} />
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <Table headers={["Pessoa", "Atribuídas", "Concluídas", "Atrasadas", "Reabertas", "SP", "Imped.", "Particip.", "Rec. concl.", "Projetos", "Sprints", "% prazo", "Ciclo méd."]} empty={data.rows.length === 0}>
        {slice(data.rows, page).map((r) => (
          <tr key={r.user_id} className="hover:bg-slate-50" data-testid={`activity-row-${r.user_id}`}>
            <td className="px-4 py-3 font-medium text-slate-900">{r.name}</td>
            <td className="px-4 py-3 text-slate-600">{r.assigned_now}</td>
            <td className="px-4 py-3 text-emerald-700 font-medium">{r.completed}</td>
            <td className="px-4 py-3 text-rose-600">{r.overdue}</td>
            <td className="px-4 py-3 text-slate-600">{r.reopened}</td>
            <td className="px-4 py-3 text-slate-600">{r.story_points}</td>
            <td className="px-4 py-3 text-amber-600">{r.impeded}</td>
            <td className="px-4 py-3 text-slate-500">{r.participations}</td>
            <td className="px-4 py-3 text-slate-500">{r.rec_done}</td>
            <td className="px-4 py-3 text-slate-500">{r.projects}</td>
            <td className="px-4 py-3 text-slate-500">{r.sprints}</td>
            <td className="px-4 py-3 text-slate-600">{r.on_time_pct != null ? `${r.on_time_pct}%` : "—"}</td>
            <td className="px-4 py-3 text-slate-600">{r.avg_cycle_days != null ? `${r.avg_cycle_days}d` : "—"}</td>
          </tr>
        ))}
      </Table>
      <Pager page={page} setPage={setPage} total={data.rows.length} />
      <ActivityNote note={data.note} />
    </div>
  );
}

const ActivityNote = ({ note }) => (
  <div className="flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid="activity-note">
    <Info className="w-4 h-4 mt-0.5 shrink-0" /> {note}
  </div>
);

const MY_CATS = [
  { key: "all", label: "Todas", fn: () => true },
  { key: "assigned_now", label: "Atribuídas agora", fn: (r) => r.assigned_now },
  { key: "done_in_period", label: "Concluídas no período", fn: (r) => r.done_in_period },
  { key: "overdue", label: "Atrasadas", fn: (r) => r.overdue },
  { key: "reopened", label: "Reabertas", fn: (r) => r.reopened_in_period },
  { key: "impeded", label: "Impedidas", fn: (r) => r.impeded },
  { key: "recurring_done", label: "Recorrentes concluídas", fn: (r) => r.recurring_done },
  { key: "participation", label: "Participações", fn: (r) => r.is_participant },
];

function MyActivityTable({ query, exportCsv }) {
  const [rows, setRows] = useState(null);
  const [cat, setCat] = useState("all");
  const [page, setPage] = useState(1);
  useEffect(() => {
    setRows(null); setPage(1);
    api.get(`/reports/activity/mine?${query}`).then(({ data }) => setRows(data.rows)).catch(() => setRows([]));
  }, [query]);
  useEffect(() => { setPage(1); }, [cat]);
  const filtered = (rows || []).filter(MY_CATS.find((c) => c.key === cat).fn);
  const exportMine = async () => {
    try {
      const res = await api.get(`/reports/activity/mine?${query}&format=csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = url; a.download = "minhas_atividades.csv"; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Falha ao exportar."); }
  };
  return (
    <div className="space-y-3" data-testid="my-activity-detail">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-heading text-lg font-semibold">Minhas atividades no período</h3>
        <button onClick={exportMine} data-testid="my-activity-export" className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-200 text-sm hover:bg-slate-50"><Download className="w-4 h-4" /> Exportar CSV</button>
      </div>
      <div className="flex flex-wrap gap-1.5" data-testid="my-activity-cats">
        {MY_CATS.map((c) => (
          <button key={c.key} onClick={() => setCat(c.key)} data-testid={`my-cat-${c.key}`}
            className={`px-2.5 py-1 rounded-md text-xs border ${cat === c.key ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{c.label}</button>
        ))}
      </div>
      {rows === null ? <div className="text-center text-slate-400 py-8">Carregando...</div> : (
        <>
          <Table headers={["Tarefa", "Projeto", "Status", "Situação atual", "Prazo", "Conclusão", "No prazo", "SP", "Recorrente", "Reab.", "Relação", ""]} empty={filtered.length === 0}>
            {slice(filtered, page).map((r) => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`my-task-row-${r.id}`}>
                <td className="px-4 py-3 font-medium text-slate-900 max-w-[220px] truncate">{r.title}</td>
                <td className="px-4 py-3 text-slate-600">{r.project_name}</td>
                <td className="px-4 py-3 text-slate-600">{STATUS_LABELS[r.status]}</td>
                <td className="px-4 py-3 text-slate-500">{r.location}</td>
                <td className="px-4 py-3 text-slate-500">{fmtDay(r.due_date)}</td>
                <td className="px-4 py-3 text-slate-500">{fmtDate(r.completed_at)}</td>
                <td className="px-4 py-3">{r.on_time == null ? "—" : r.on_time ? <span className="text-emerald-600">Sim</span> : <span className="text-rose-600">Não</span>}</td>
                <td className="px-4 py-3 text-slate-600">{r.story_points ?? "—"}</td>
                <td className="px-4 py-3">{r.recurring ? "Sim" : "Não"}</td>
                <td className="px-4 py-3 text-slate-600">{r.reopen_count}</td>
                <td className="px-4 py-3 text-slate-500">{r.is_assignee ? "Responsável" : "Participante"}</td>
                <td className="px-4 py-3"><Link to={`/projetos/${r.project_id}/backlog`} className="text-slate-400 hover:text-slate-800" data-testid={`my-task-link-${r.id}`}><ExternalLink className="w-4 h-4" /></Link></td>
              </tr>
            ))}
          </Table>
          <Pager page={page} setPage={setPage} total={filtered.length} />
        </>
      )}
    </div>
  );
}
